import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableColumn,
	ContentTableItem
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useQuickLinks from "../../hooks/useQuickLinks";
import useDeleteQuickLink from "../../hooks/useDeleteQuickLink";
import useToggleQuickLinkPublished from "../../hooks/useToggleQuickLinkPublished";
import useMoveQuickLink from "../../hooks/useMoveQuickLink";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";
import AudienceCell from "src/components/AudienceCell/AudienceCell";

interface QuickLinkTableItem extends ContentTableItem {
	data: {
		audienceGroups: { id: number; name: string }[];
	};
}

function mapQuickLinksToContentTableItems(
	activeLinks: QuickLink[],
	draftLinks: QuickLink[]
): QuickLinkTableItem[] {
	const live = [...activeLinks]
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
		.map((link, index) => ({
			id: link.id,
			name: link.heading,
			createdBy: link.createdBy
				? `${link.createdBy.firstName} ${link.createdBy.lastName}`
				: undefined,
			lastUpdated: link.updatedAt ?? link.createdAt,
			isActive: true,
			order: index + 1,
			data: { audienceGroups: link.audienceGroups ?? [] }
		}));

	const draft = draftLinks.map((link) => ({
		id: link.id,
		name: link.heading,
		createdBy: link.createdBy
			? `${link.createdBy.firstName} ${link.createdBy.lastName}`
			: undefined,
		lastUpdated: link.updatedAt ?? link.createdAt,
		isActive: false,
		data: { audienceGroups: link.audienceGroups ?? [] }
	}));

	return [...live, ...draft];
}

const QuickLinks = () => {
	const navigate = useNavigate();
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);
	const [filterGroupId, setFilterGroupId] = useState<
		number | "all" | "everyone"
	>("all");

	const { data: activeLinks = [] } = useQuickLinks(true);
	const { data: draftLinks = [] } = useQuickLinks(false);

	const { data: adminGroups = [] } = useAdminGroups();

	// If the currently-selected group disappears from adminGroups (deleted by
	// another admin while this page is open), reset the filter so the user
	// isn't stuck filtered to an invisible option.
	useEffect(() => {
		if (typeof filterGroupId !== "number") return;
		if (!adminGroups.some((g) => Number(g.id) === filterGroupId)) {
			setFilterGroupId("all");
		}
	}, [adminGroups, filterGroupId]);

	const { mutate: togglePublished } = useToggleQuickLinkPublished();
	const { mutate: deleteQuickLink, isLoading: isDeleting } =
		useDeleteQuickLink();
	const { mutate: moveQuickLink } = useMoveQuickLink();

	const items = useMemo(() => {
		const all = mapQuickLinksToContentTableItems(activeLinks, draftLinks);
		if (filterGroupId === "all") return all;
		if (filterGroupId === "everyone")
			return all.filter((item) => item.data.audienceGroups.length === 0);
		return all.filter((item) =>
			item.data.audienceGroups.some((g) => g.id === filterGroupId)
		);
	}, [activeLinks, draftLinks, filterGroupId]);

	const handleMove = (item: ContentTableItem, direction: -1 | 1) => {
		moveQuickLink({ id: Number(item.id), offset: direction });
	};

	const columns = useMemo<ContentTableColumn[]>(
		() => [
			{
				key: "createdBy",
				label: "Created by",
				render: (item) => item.createdBy ?? "—"
			},
			{
				key: "lastUpdated",
				label: "Last updated",
				render: (item) => item.lastUpdated ?? "—"
			},
			{
				key: "audience",
				label: "Audience",
				render: (item) => {
					const groups =
						(item.data as { audienceGroups?: { id: number; name: string }[] })
							?.audienceGroups ?? [];
					return <AudienceCell groups={groups} />;
				}
			},
			{
				key: "order",
				label: "Order",
				width: "56px",
				render: (item) =>
					item.isActive && item.order !== null && item.order !== undefined ? String(item.order) : "—"
			}
		],
		[]
	);

	const selectedFilterLabel =
		filterGroupId === "all"
			? null
			: filterGroupId === "everyone"
				? "Everyone"
				: (adminGroups.find((g) => Number(g.id) === filterGroupId)?.name ??
					"group");
	const filterAnnouncement =
		filterGroupId === "all"
			? ""
			: `Showing ${items.length} quick link${items.length === 1 ? "" : "s"} filtered by ${selectedFilterLabel}`;

	return (
		<Page>
			<p className="sr-only" aria-live="polite">
				{filterAnnouncement}
			</p>
			<ContentTable
				items={items}
				columns={columns}
				onRowClick={(item) => navigate(`/app/homepage/quick-links/${item.id}`)}
				onMakeLive={(item) =>
					togglePublished({ id: Number(item.id), published: true })
				}
				onMakeDraft={(item) =>
					togglePublished({ id: Number(item.id), published: false })
				}
				onDelete={(item) => setItemToDelete(item)}
				canDelete={(item) => !item.isActive}
				onMoveUp={(item) => handleMove(item, -1)}
				onMoveDown={(item) => handleMove(item, 1)}
				createPath="/app/homepage/quick-links/create"
				createLabel="Create Quick Link"
				searchRowExtra={
					<label className="flex items-center gap-2 text-sm">
						<span className="text-muted-foreground">Filter by group</span>
						<select
							value={filterGroupId}
							onChange={(e) => {
								const v = e.target.value;
								if (v === "all" || v === "everyone") setFilterGroupId(v);
								else setFilterGroupId(Number(v));
							}}
							className="rounded-lg border border-border bg-white px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring"
						>
							<option value="all">All groups</option>
							<option value="everyone">Everyone (no restrictions)</option>
							{adminGroups.map((g) => (
								<option key={g.id} value={Number(g.id)}>
									{g.name}
								</option>
							))}
						</select>
					</label>
				}
			/>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete quick link?"
				description="This action cannot be undone."
				onCancel={() => setItemToDelete(null)}
				onSubmit={() => {
					if (itemToDelete) {
						deleteQuickLink(Number(itemToDelete.id));
						setItemToDelete(null);
					}
				}}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default QuickLinks;
